/**
 * FRODO Route Selection Modal
 * 
 * Modal for selecting which fixed routes to consider for FRODO optimization.
 * Follows pattern from ImportGTFSModal.tsx for consistent styling.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { useFixedRoutes } from '../../context/FixedRoutesContext';

interface FrodoRouteSelectionModalProps {
  open: boolean;
  onClose: () => void;
  selectedRouteIds: string[];
  onSelectionChange: (selectedRouteIds: string[]) => void;
}

export default function FrodoRouteSelectionModal({
  open,
  onClose,
  selectedRouteIds,
  onSelectionChange,
}: FrodoRouteSelectionModalProps) {
  const { t } = useTranslation(['simulation', 'common']);
  const { routes } = useFixedRoutes();
  const [localSelection, setLocalSelection] = useState<Set<string>>(new Set(selectedRouteIds));

  // Sync local selection with prop when modal opens
  useEffect(() => {
    if (open) {
      setLocalSelection(new Set(selectedRouteIds));
    }
  }, [open, selectedRouteIds]);

  const handleToggleRoute = (routeId: string) => {
    setLocalSelection((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) {
        next.delete(routeId);
      } else {
        next.add(routeId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (localSelection.size === routes.length) {
      // Deselect all
      setLocalSelection(new Set());
    } else {
      // Select all
      setLocalSelection(new Set(routes.map((route) => route.id)));
    }
  };

  const handleApply = () => {
    onSelectionChange(Array.from(localSelection));
    onClose();
  };

  const handleCancel = () => {
    // Reset to original selection
    setLocalSelection(new Set(selectedRouteIds));
    onClose();
  };

  const selectedCount = localSelection.size;
  const totalCount = routes.length;
  const allSelected = routes.length > 0 && localSelection.size === routes.length;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{t('modal.frodoRoutesTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Selection Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {t('setup.frodo.modal.summary', { selected: selectedCount, total: totalCount })}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAll}
            >
              {allSelected ? t('setup.frodo.modal.deselectAll') : t('setup.frodo.modal.selectAll')}
            </Button>
          </Box>

          {/* Route List */}
          {routes.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('setup.frodo.modal.noRoutes')}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <List dense>
                {routes.map((route) => {
                  const isSelected = localSelection.has(route.id);
                  const stopCount = route.stops.length;

                  return (
                    <ListItem key={route.id} disablePadding>
                      <ListItemButton onClick={() => handleToggleRoute(route.id)}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Box>
                                <Typography variant="body1">{route.name}</Typography>
                                {route.shortName && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                                    {route.shortName}
                                  </Typography>
                                )}
                              </Box>
                              {route.isCirculator && (
                                <Chip label={t('setup.frodo.modal.circulator')} size="small" color="primary" variant="outlined" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('setup.frodo.modal.id', { id: route.id })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('setup.frodo.modal.time', { start: route.startTime, end: route.endTime ? ` - ${route.endTime}` : ' - 22:00' })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('setup.frodo.modal.stops', { count: stopCount })}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('common:cancel')}</Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={localSelection.size === 0}
        >
          {t('setup.frodo.modal.apply', { count: selectedCount })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

